import { put, select, call } from 'typed-redux-saga'
import { AnyAction } from 'redux'

import services from '@services'
import { showError } from '@store/ui/error/actions'
import { setLastServiceResponse } from '@store/session/actions'
import { ApplicationState } from '@store/types'
import { setDocumentos, setIdIteracao } from '@store/refinanciamento/documentosAssinatura/actions'
import { BridgeService } from '@services/bridge'
import { HttpStatus } from '@services/base'
import { DocumentoAssinaturaRequest } from '@services/getSignatureDocumentsService'
import { logout } from '@utils/functions'
import { ErroType } from '@utils/types'
import type { DocumentosAssinaturaState } from '@store/refinanciamento/documentosAssinatura/types'

import { hideLoading, signatureDocuments, showLoading } from '../actions'

interface Props {
  noRemoveLoader: boolean
  actionCallbackError: {
    message: string
    actionCallback: () => AnyAction
    actionTitle: string
  }
  type: string
}

export default function* signatureDocumentsSaga({
  noRemoveLoader,
  actionCallbackError = {
    message: 'Aguarde um instante e tente novamente.',
    actionCallback: () => signatureDocuments(),
    actionTitle: 'Tentar Novamente',
  },
}: Props) {
  const appState = yield* select((state: ApplicationState) => state)
  try {
    let docs: DocumentosAssinaturaState['documentos']
    let data: DocumentoAssinaturaRequest
    let status: number

    yield put(showLoading())

    const propostaAntiga = appState.refinanciamento.contratos.contratosSelecionados.map(
      (item) => item.numeroProposta,
    )[0]

    if (BridgeService.isBrowser()) {
      const response = yield* call(
        services.getSignatureDocumentsService,
        appState.session.accessToken,
      )
      data = response?.data
      status = response?.status
    } else {
      const response = yield* call(
        services.postSignatureDocumentsService,
        {
          cpf: appState.refinanciamento.dadosPessoais.cpfDadosPessoais ?? '',
          numeroProposta:
            String(appState.refinanciamento.proposta.numeroProposta) || propostaAntiga,
        },
        appState.session.accessToken,
      )
      data = response?.data
      status = response?.status
    }

    if (data) {
      const docsEnviados = data.enviados.map((doc) => ({
        ...doc,
        isDone: true,
      }))
      const docsPendentes = data.pendentes ?? []
      docs = [...docsEnviados, ...docsPendentes] as DocumentosAssinaturaState['documentos']
      yield put(setDocumentos(docs))
      yield put(setIdIteracao(data?.idInteracao))
    }

    if (!noRemoveLoader) {
      yield put(hideLoading())
    }

    if (data && status === 200) {
      yield put(
        setLastServiceResponse({
          code: 'OK',
          serviceName: BridgeService.isBrowser()
            ? 'getSignatureDocumentsService'
            : 'postSignatureDocumentsService',
        }),
      )
      return true
    }

    yield put(showError(actionCallbackError))

    return false
  } catch (error) {
    yield put(
      setLastServiceResponse({
        code: 'ERROR',
        serviceName: BridgeService.isBrowser()
          ? 'getSignatureDocumentsService'
          : 'postSignatureDocumentsService',
      }),
    )
    const erroTyped = error as ErroType
    if (BridgeService.isBrowser() && erroTyped.status === HttpStatus.UNAUTHORIZED) {
      yield put(
        showError({
          title: 'Sessão expirada',
          message: 'Efetue o login novamente.',
          actionTitle: 'Continuar',
          functionCallback: () => logout(),
        }),
      )
      return false
    }
    yield put(hideLoading())
    yield put(showError(actionCallbackError))
    return false
  }
}
